// Transcript file import (Figma: attach a file / drop it into a box). Reads plain-text
// transcripts client-side; the Worker's Input Guard validates the content server-side.

const TEXT_EXT = ['.txt', '.text', '.vtt', '.srt', '.md', '.log', '.csv'];

export interface ImportedFile {
  name: string;
  text: string;
}

export class ImportError extends Error {}

export function isLikelyText(name: string): boolean {
  const lower = name.toLowerCase();
  return TEXT_EXT.some((e) => lower.endsWith(e)) || !lower.includes('.');
}

export function readTranscriptFile(file: File): Promise<ImportedFile> {
  return new Promise((resolve, reject) => {
    if (!isLikelyText(file.name)) {
      reject(new ImportError(`"${file.name}" isn't a plain-text transcript. Use .txt, .vtt, .srt, or .md.`));
      return;
    }
    if (file.size > 5_000_000) {
      reject(new ImportError('That file is too large (max ~5 MB of text).'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new ImportError('Could not read that file.'));
    reader.onload = () => {
      const text = String(reader.result ?? '');
      // Reject binary-looking content (e.g. a .docx renamed to .txt): count control bytes
      // excluding tab/newline/carriage-return.
      const control = (text.slice(0, 2000).match(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g) || []).length;
      if (control > 8) {
        reject(new ImportError("That doesn't look like a plain-text transcript."));
        return;
      }
      resolve({ name: file.name, text });
    };
    reader.readAsText(file);
  });
}

/** Wire a button + a drop zone to a single handler. */
export function wireFileImport(opts: {
  button: HTMLElement;
  dropZone: HTMLElement;
  input: HTMLInputElement;
  onFile: (f: ImportedFile) => void;
  onError: (msg: string) => void;
  /** Runs on the Import button click BEFORE the picker opens; return false to suppress it (e.g. a
   *  billing gate has already shown its own message). Must be synchronous — the file dialog has to open
   *  inside the click gesture, so it can't wait on an async check. */
  beforeOpen?: () => boolean;
}): void {
  const handle = async (file: File | undefined) => {
    if (!file) return;
    try {
      opts.onFile(await readTranscriptFile(file));
    } catch (e) {
      opts.onError(e instanceof Error ? e.message : 'Import failed.');
    }
  };
  // Import one transcript at a time. If several files arrive at once (a multi-file
  // drop), take the first and tell the user the rest were skipped rather than
  // dropping them silently. The warning is emitted AFTER the import starts, because
  // starting an import clears the notification log — a warning shown beforehand
  // would be wiped out.
  const handleList = async (files: FileList | null | undefined) => {
    const first = files?.[0];
    if (!first) return;
    const skipped = files && files.length > 1 ? files.length - 1 : 0;
    await handle(first);
    if (skipped > 0) {
      const noun = skipped === 1 ? 'file' : 'files';
      opts.onError(
        `${skipped} other ${noun} skipped, only "${first.name}" was imported. Import transcripts one at a time.`,
      );
    }
  };
  opts.button.addEventListener('click', () => {
    if (opts.beforeOpen && !opts.beforeOpen()) return; // gate handled it (e.g. out of calls) → no picker
    opts.input.click();
  });
  opts.input.addEventListener('change', () => {
    void handleList(opts.input.files);
    opts.input.value = '';
  });
  const dz = opts.dropZone;
  ['dragenter', 'dragover'].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      e.preventDefault();
      dz.classList.add('drop-active');
    }),
  );
  ['dragleave', 'drop'].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      e.preventDefault();
      dz.classList.remove('drop-active');
    }),
  );
  dz.addEventListener('drop', (e) => {
    void handleList((e as DragEvent).dataTransfer?.files);
  });
}
